import React, { useEffect, useMemo, useRef } from 'react';
import { Link } from 'react-router-dom';
import {
  TimelineAssumptions,
  OngoingTrialTimelineInput,
  computeTrialMilestones,
  DEFAULT_TIMELINE_VIEW_YEAR,
  formatRegimenSnippet,
  formatTimelineDate,
  isCombinationTrial,
  monthIndex,
  phaseShortLabel,
  trialInstitutionLabel,
  trialPrimaryAssetId,
} from '../../utils/ongoingTrialsTimeline';

const PX_PER_MONTH = 13;
const LABEL_WIDTH = 200;
const ROW_HEIGHT = 68;
const BAR_HEIGHT = 40;
const TRACK_PADDING = 8;

interface OngoingTrialsGanttProps {
  trials: OngoingTrialTimelineInput[];
  assumptions: TimelineAssumptions;
  acceleratedTrialIds: Set<number>;
  onToggleAccelerated: (trialId: number) => void;
}

function dateToX(date: Date, rangeStart: Date): number {
  return monthIndex(date, rangeStart) * PX_PER_MONTH;
}

function MilestoneMarker({
  x,
  kind,
  label,
}: {
  x: number;
  kind: 'start' | 'readout' | 'studyComplete' | 'filing' | 'approval';
  label?: string;
}) {
  const y = ROW_HEIGHT / 2;
  if (kind === 'start') {
    return (
      <polygon
        points={`${x},${y - 6} ${x + 6},${y} ${x},${y + 6} ${x - 6},${y}`}
        fill="#ea580c"
        stroke="#c2410c"
        strokeWidth="1"
      />
    );
  }
  if (kind === 'readout') {
    return (
      <polygon
        points={`${x},${y - 7} ${x + 7},${y + 5} ${x - 7},${y + 5}`}
        fill="#991b1b"
        stroke="#7f1d1d"
        strokeWidth="1"
      />
    );
  }
  if (kind === 'studyComplete') {
    return (
      <g>
        <rect x={x - 6} y={y - 6} width={12} height={12} rx={1} fill="#1d4ed8" stroke="#1e3a8a" strokeWidth="1" />
        {label && (
          <text x={x} y={y + 18} textAnchor="middle" className="fill-blue-800 text-[8px] font-semibold">
            {label}
          </text>
        )}
      </g>
    );
  }
  if (kind === 'filing') {
    return (
      <polygon
        points={`${x},${y - 7} ${x + 2.5},${y - 2} ${x + 7},${y - 2} ${x + 3.5},${y + 1.5} ${x + 5},${y + 7} ${x},${y + 4} ${x - 5},${y + 7} ${x - 3.5},${y + 1.5} ${x - 7},${y - 2} ${x - 2.5},${y - 2}`}
        fill="#dc2626"
        stroke="#b91c1c"
        strokeWidth="0.75"
      />
    );
  }
  return (
    <g>
      <polygon
        points={`${x},${y - 7} ${x + 5},${y} ${x},${y + 7} ${x - 5},${y}`}
        fill="#16a34a"
        stroke="#15803d"
        strokeWidth="1"
      />
      {label && (
        <text x={x} y={y + 18} textAnchor="middle" className="fill-slate-600 text-[8px] font-semibold">
          {label}
        </text>
      )}
    </g>
  );
}

function TrialGanttRow({
  trial,
  rangeStart,
  trackWidth,
  assumptions,
  accelerated,
  onToggleAccelerated,
}: {
  trial: OngoingTrialTimelineInput;
  rangeStart: Date;
  trackWidth: number;
  assumptions: TimelineAssumptions;
  accelerated: boolean;
  onToggleAccelerated: () => void;
}) {
  const milestones = useMemo(
    () => computeTrialMilestones(trial, assumptions, accelerated),
    [trial, assumptions, accelerated],
  );
  const combo = isCombinationTrial(trial);
  const regimen = formatRegimenSnippet(trial);
  const title = trial.trial_name || trial.full_title || trial.nct_id || `Trial #${trial.id}`;
  const assetId = trialPrimaryAssetId(trial);
  const assetName = trial.asset_name || (assetId != null ? `Asset #${assetId}` : 'Unlinked asset');
  const institution = trialInstitutionLabel(trial);

  const barStart = milestones.start ? dateToX(milestones.start, rangeStart) : null;
  const barEndDate = milestones.studyCompletion || milestones.primaryCompletion;
  const barEnd = barEndDate ? dateToX(barEndDate, rangeStart) : null;
  const barWidth = barStart != null && barEnd != null ? Math.max(barEnd - barStart, 28) : null;
  const barX = barStart != null ? barStart + TRACK_PADDING : null;
  const barY = ROW_HEIGHT / 2 - BAR_HEIGHT / 2;

  const readoutX = milestones.dataReadout ? dateToX(milestones.dataReadout, rangeStart) : null;
  const studyCompleteX = milestones.studyCompletion
    ? dateToX(milestones.studyCompletion, rangeStart)
    : null;
  const filingX = milestones.filing ? dateToX(milestones.filing, rangeStart) : null;
  const usX = milestones.approvalUs ? dateToX(milestones.approvalUs, rangeStart) : null;
  const euX = milestones.approvalEu ? dateToX(milestones.approvalEu, rangeStart) : null;

  const barFill = milestones.isCompleted ? (combo ? '#f8fafc' : '#fef9c3') : combo ? '#ffffff' : '#fef08a';
  const barStroke = milestones.isCompleted ? '#64748b' : combo ? '#1e293b' : '#ca8a04';

  const detailParts = [
    phaseShortLabel(trial.phases),
    combo ? 'Combo' : 'Mono',
    regimen || null,
    milestones.isCompleted && milestones.studyCompletion
      ? `Done ${formatTimelineDate(milestones.studyCompletion)}`
      : null,
  ].filter(Boolean);

  return (
    <div className="flex border-b border-slate-100 last:border-b-0" style={{ minHeight: ROW_HEIGHT }}>
      <div
        className="sticky left-0 z-10 shrink-0 border-r border-slate-200 bg-white px-3 py-2 flex flex-col justify-center gap-0.5 shadow-[4px_0_8px_-4px_rgba(15,23,42,0.12)]"
        style={{ width: LABEL_WIDTH }}
      >
        {assetId != null ? (
          <Link to={`/emerging-assets/${assetId}`} className="text-xs font-semibold text-slate-900 leading-snug line-clamp-2 hover:text-indigo-700">
            {assetName}
          </Link>
        ) : (
          <span className="text-xs font-semibold text-slate-900 leading-snug line-clamp-2">{assetName}</span>
        )}
        {institution && (
          <p className="text-[10px] text-slate-500 line-clamp-2 leading-snug" title={institution}>
            {institution}
          </p>
        )}
        <div className="flex items-center gap-1.5 flex-wrap mt-0.5">
          {milestones.isCompleted && (
            <span className="inline-flex rounded border border-blue-200 bg-blue-50 px-1.5 py-0.5 text-[9px] font-bold text-blue-800">
              Completed
            </span>
          )}
          {milestones.showProjectedMilestones && (
            <label className="inline-flex items-center gap-1 text-[9px] text-slate-500 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={accelerated}
                onChange={onToggleAccelerated}
                className="rounded border-slate-300 text-indigo-600 focus:ring-indigo-500 w-3 h-3"
              />
              Accel.
            </label>
          )}
        </div>
      </div>
      <div className="relative bg-slate-50/40" style={{ width: trackWidth, minWidth: trackWidth }}>
        <svg width={trackWidth} height={ROW_HEIGHT} className="block">
          {barX != null && barWidth != null && (
            <>
              <MilestoneMarker x={barX} kind="start" />
              <rect
                x={barX}
                y={barY}
                width={barWidth}
                height={BAR_HEIGHT}
                rx={4}
                fill={barFill}
                stroke={barStroke}
                strokeWidth={1.5}
                strokeDasharray={milestones.isCompleted ? '4 2' : undefined}
              />
              <foreignObject x={barX + 2} y={barY + 2} width={Math.max(barWidth - 4, 0)} height={BAR_HEIGHT - 4}>
                <div
                  xmlns="http://www.w3.org/1999/xhtml"
                  className="h-full overflow-hidden px-1 py-0.5 flex flex-col justify-center"
                >
                  <Link
                    to={`/trials/${trial.id}`}
                    className="text-[9px] font-semibold text-slate-900 leading-tight line-clamp-2 hover:text-indigo-700"
                    title={title}
                  >
                    {title}
                  </Link>
                  <p className="text-[8px] text-slate-600 leading-tight line-clamp-1 mt-0.5" title={detailParts.join(' · ')}>
                    {detailParts.join(' · ')}
                  </p>
                  {trial.nct_id && (
                    <a
                      href={`https://clinicaltrials.gov/study/${trial.nct_id}`}
                      target="_blank"
                      rel="noreferrer"
                      className="text-[8px] font-mono text-indigo-600 hover:underline mt-0.5 truncate"
                    >
                      {trial.nct_id}
                    </a>
                  )}
                </div>
              </foreignObject>
            </>
          )}
          {readoutX != null && <MilestoneMarker x={readoutX + TRACK_PADDING} kind="readout" />}
          {studyCompleteX != null && (
            <MilestoneMarker x={studyCompleteX + TRACK_PADDING} kind="studyComplete" label="Done" />
          )}
          {filingX != null && <MilestoneMarker x={filingX + TRACK_PADDING} kind="filing" />}
          {usX != null && <MilestoneMarker x={usX + TRACK_PADDING} kind="approval" label="US" />}
          {euX != null && <MilestoneMarker x={euX + TRACK_PADDING} kind="approval" label="EU" />}
          {!milestones.start && !milestones.primaryCompletion && !milestones.studyCompletion && (
            <text x={12} y={ROW_HEIGHT / 2 + 4} className="fill-slate-400 text-[11px]">
              Dates unavailable
            </text>
          )}
        </svg>
      </div>
    </div>
  );
}

const OngoingTrialsGantt: React.FC<OngoingTrialsGanttProps> = ({
  trials,
  assumptions,
  acceleratedTrialIds,
  onToggleAccelerated,
}) => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const didDefaultScroll = useRef(false);

  const { rangeStart, totalMonths, years } = useMemo(() => {
    const dates: Date[] = [];
    for (const trial of trials) {
      const m = computeTrialMilestones(
        trial,
        assumptions,
        acceleratedTrialIds.has(trial.id) || false,
      );
      [m.start, m.primaryCompletion, m.studyCompletion, m.filing, m.approvalUs, m.approvalEu].forEach((d) => {
        if (d) dates.push(d);
      });
    }

    const minYear =
      dates.length > 0
        ? Math.min(...dates.map((d) => d.getFullYear()))
        : DEFAULT_TIMELINE_VIEW_YEAR;
    const maxYear =
      dates.length > 0
        ? Math.max(...dates.map((d) => d.getFullYear()))
        : DEFAULT_TIMELINE_VIEW_YEAR + 5;

    const startYear = Math.min(minYear, DEFAULT_TIMELINE_VIEW_YEAR);
    const endYear = Math.max(maxYear + 1, DEFAULT_TIMELINE_VIEW_YEAR + 5);
    const start = new Date(startYear, 0, 1);
    const end = new Date(endYear, 11, 31);
    const months = monthIndex(end, start) + 1;
    const yearList: number[] = [];
    for (let y = start.getFullYear(); y <= end.getFullYear(); y += 1) yearList.push(y);
    return { rangeStart: start, totalMonths: months, years: yearList };
  }, [trials, assumptions, acceleratedTrialIds]);

  const trackWidth = totalMonths * PX_PER_MONTH + TRACK_PADDING * 2;

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const targetX = dateToX(new Date(DEFAULT_TIMELINE_VIEW_YEAR, 0, 1), rangeStart);
    if (!didDefaultScroll.current) {
      el.scrollLeft = Math.max(0, targetX);
      didDefaultScroll.current = true;
      return;
    }
    if (el.scrollLeft < targetX - 4) {
      el.scrollLeft = Math.max(0, targetX);
    }
  }, [rangeStart, trials.length]);

  if (trials.length === 0) {
    return (
      <div className="px-6 py-12 text-center text-sm text-slate-500">No trials to display on the timeline.</div>
    );
  }

  return (
    <div className="border border-slate-200 rounded-2xl overflow-hidden bg-white">
      <div className="px-4 py-3 bg-slate-50 border-b border-slate-200 flex flex-wrap gap-x-5 gap-y-2 text-[10px] text-slate-600">
        <span className="inline-flex items-center gap-1.5">
          <span className="inline-block w-4 h-3 rounded-sm bg-yellow-200 border border-yellow-600" /> Monotherapy study
        </span>
        <span className="inline-flex items-center gap-1.5">
          <span className="inline-block w-4 h-3 rounded-sm bg-white border border-slate-800" /> Combination study
        </span>
        <span className="inline-flex items-center gap-1.5">
          <span className="text-orange-600">◆</span> Study start
        </span>
        <span className="inline-flex items-center gap-1.5">
          <span className="text-red-800">▲</span> Primary completion (data readout)
        </span>
        <span className="inline-flex items-center gap-1.5">
          <span className="inline-block w-3 h-3 rounded-sm bg-blue-700 border border-blue-900" /> Study completion (actual)
        </span>
        <span className="inline-flex items-center gap-1.5">
          <span className="text-red-600">★</span> Filing (projected, ongoing trials)
        </span>
        <span className="inline-flex items-center gap-1.5">
          <span className="text-green-600">◆</span> Approval (projected)
        </span>
      </div>

      <div ref={scrollRef} className="max-h-[70vh] overflow-auto">
        <div style={{ width: LABEL_WIDTH + trackWidth }}>
          <div className="flex sticky top-0 z-20 border-b border-slate-200 bg-slate-50">
            <div
              className="sticky left-0 z-30 shrink-0 px-3 py-2 text-[10px] font-bold uppercase tracking-wide text-slate-500 border-r border-slate-200 bg-slate-50 shadow-[4px_0_8px_-4px_rgba(15,23,42,0.12)]"
              style={{ width: LABEL_WIDTH }}
            >
              Asset / institution
            </div>
            <div className="flex h-8" style={{ width: trackWidth }}>
              {years.map((year) => {
                const x = dateToX(new Date(year, 0, 1), rangeStart);
                const nextX = dateToX(new Date(year + 1, 0, 1), rangeStart);
                const w = nextX - x;
                return (
                  <div
                    key={year}
                    className="shrink-0 text-center text-[11px] font-bold text-slate-600 border-r border-slate-200 flex items-center justify-center bg-slate-50"
                    style={{ width: w }}
                  >
                    {year}
                  </div>
                );
              })}
            </div>
          </div>

          {trials.map((trial) => (
            <TrialGanttRow
              key={trial.id}
              trial={trial}
              rangeStart={rangeStart}
              trackWidth={trackWidth}
              assumptions={assumptions}
              accelerated={acceleratedTrialIds.has(trial.id)}
              onToggleAccelerated={() => onToggleAccelerated(trial.id)}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

export default OngoingTrialsGantt;
